
import { OptionsClass } from "@/domain/entities/MainApp";
import { Options, Vue, prop } from "vue-class-component";
import CustomDropdown from "@/app/ui/components/custom-dropdown/index.vue";
import Checkbox from "primevue/checkbox";
import {
  LastLocationOption,
} from "@/domain/entities/UrgentDeliveryManagement";
import { UrgentDeliveryManagementController } from "@/app/ui/controllers/UrgentDeliveryManagementController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ProductController } from "@/app/ui/controllers/ProductController";
import DateRangePicker from "@/app/ui/components/date-range-picker/index.vue";
import { Range } from "@/app/ui/components/date-range-picker/type";
import { capitalize, debounce } from "lodash";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { firstCapitalize } from "@/app/infrastructures/misc/Utils";

class Props {
  currentParameters = prop<any>({
    required: true
  });
}

@Options({
  components: {
    CustomDropdown,
    Checkbox,
    DateRangePicker
  },
  emits: ["apply", "reset"],
  directives: {
    "click-outside": {
      beforeMount: function(el, binding) {
        const ourClickEventHandler = (event: any) => {
          if (!document.body.contains(event.target)) return;
          if (event.target.closest?.("#date-range-picker")) return;

          if (!el.contains(event.target) && el !== event.target) {
            binding.value(event);
          }
        };
        el.__vueClickEventHandler__ = ourClickEventHandler;
        document.addEventListener("click", ourClickEventHandler);
      },
      unmounted: function(el) {
        document.removeEventListener("click", el.__vueClickEventHandler__);
      }
    }
  }
})
export default class UrgentRelabelFilter extends Vue.with(Props) {
  isOpen = false;

  tempParameters = {
    clientAggregatorType: new OptionsClass()
  };

  selectedProductTypes: string[] = [];
  productTypeModel: string[] = [];
  productTypeSelectedCount = "";

  tempOriginCity: OptionsClass | null = null;
  tempDestinationCity: OptionsClass | null = null;
  dateRangeModel = new Range();

  get clientAggregatorTypeOptions(): OptionsClass[] {
    return [
      new OptionsClass({
        name: this.$t("urgentRelabel.filter.optionClient"),
        value: "aggregator"
      }),
      new OptionsClass({
        name: this.$t("urgentRelabel.filter.optionConsolidator"),
        value: "non_aggregator"
      })
    ];
  }

  get productListData(): string[] {
    const defaultOption = this.$t("urgentRelabel.filter.allProducts");
    const products = ProductController.productData.data;
    if (products.length > 0) {
      const newProducts = products.map((e: any) => e.name);
      return newProducts;
    }
    return [defaultOption];
  }

  get cityOptions(): LastLocationOption[] {
    return UrgentDeliveryManagementController.lastLocationOptions;
  }

  get isLoadingCity() {
    return LocationController.isLoadingDestinationCity;
  }

  get optionDestinationCity() {
    return LocationController.cityData.cityData.map(
      item =>
        new OptionsClass({
          id: item.id,
          label: `${item.code} - ${firstCapitalize(item.name?.toLowerCase())}`,
          code: item.code,
          name: capitalize(item.name?.toLowerCase())
        })
    );
  }

  async fetchProductList() {
    return ProductController.getProductList({
      page: 1,
      limit: 99,
      search: "",
      status: "active",
      productCode: ""
    });
  }

  onProductTypeChange() {
    const count = this.selectedProductTypes.length;
    this.productTypeSelectedCount =
      count > 0
        ? this.$t("urgentRelabel.filter.productCount", { count })
        : "";
    this.productTypeModel = [...this.selectedProductTypes];
  }

  get filterCount(): number {
    const current = this.currentParameters;
    let count = 0;

    if (current.clientAggregatorType) count++;
    if (current.productType) count++;
    if (current.originHubId) count++;
    if (current.destinationHubId) count++;

    return count;
  }

  toggleFilter() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.syncTempFromCurrent();
    }
  }

  closeFilter() {
    if (this.isOpen) {
      this.isOpen = false;
    }
  }

  syncTempFromCurrent() {
    const current = this.currentParameters;
    this.dateRangeModel = new Range({
      start: current.startDateRaw || null,
      end: current.endDateRaw || null
    });

    this.tempParameters.clientAggregatorType = current.clientAggregatorType
      ? new OptionsClass(
          this.clientAggregatorTypeOptions.find(
            (o) => o.value === current.clientAggregatorType
          ) || {}
        )
      : new OptionsClass();

    this.selectedProductTypes = current.productType
      ? current.productType.split(",")
      : [];
    this.onProductTypeChange();

  }

  selectTempFilter(data: OptionsClass, type: string) {
    (this.tempParameters as any)[type] = data;
  }

  resetFilter() {
    this.tempParameters.clientAggregatorType = new OptionsClass();
    this.selectedProductTypes = [];
    this.productTypeModel = [];
    this.productTypeSelectedCount = "";
    this.tempOriginCity = null;
    this.tempDestinationCity = null;
    this.dateRangeModel = new Range();

    const originRef = this.$refs.originCityFilterRef as any;
    if (originRef?.reset) originRef.reset();

    const destinationRef = this.$refs.destinationCityFilterRef as any;
    if (destinationRef?.reset) destinationRef.reset();

    this.$emit("reset");
    this.isOpen = false;
  }

  applyFilter() {
    this.$emit("apply", {
      clientAggregatorType: this.tempParameters.clientAggregatorType,
      productType: this.selectedProductTypes,
      originCityCodes: this.tempOriginCity?.code ? [this.tempOriginCity.code] : [],
      destinationCityCodes: this.tempDestinationCity?.code ? [this.tempDestinationCity.code] : [],
      dateRange: this.dateRangeModel
    });
    this.isOpen = false;
  }

  fetchDestinationCity = debounce((search: string) => {
    LocationController.getCityList({
      search: search,
      status: "",
      page: 1,
      limit: 10
    });
  }, 250);

  async mounted() {
    UrgentDeliveryManagementController.resetLastLocationOptions();
    UrgentDeliveryManagementController.getLastLocationOptions();
    await this.fetchProductList();
  }
}
